import { useState, type ChangeEvent, type FormEvent } from 'react'
import { ApiError } from '@/api/errors'
import type { ReportKind } from '@/api/types'
import { reportKindLabel } from '@/i18n/labels'
import { Button, Field, Select, Sheet, Spinner, Textarea } from '@/ui'
import { useUploadFile } from '@/features/files/queries'
import { useCreateReport } from './queries'

const KINDS: ReportKind[] = ['PROGRESS', 'COMPLETION', 'QC_RESPONSE', 'ISSUE', 'OTHER']

interface Upload {
  name: string
  fileId?: string
  error?: string
}

export function ReportCreateSheet({
  orderId,
  open,
  onClose,
}: {
  orderId: string
  open: boolean
  onClose: () => void
}) {
  const create = useCreateReport(orderId)
  const upload = useUploadFile()
  const [kind, setKind] = useState<ReportKind>('PROGRESS')
  const [comment, setComment] = useState('')
  const [uploads, setUploads] = useState<Upload[]>([])

  const uploading = upload.isPending
  const fileIds = uploads.flatMap((u) => (u.fileId ? [u.fileId] : []))
  const canSubmit = comment.trim().length > 0 || fileIds.length > 0
  const errMessage = create.error instanceof ApiError ? create.error.message : null

  async function onFiles(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    for (const file of files) {
      const idx = uploads.length
      setUploads((prev) => [...prev, { name: file.name }])
      try {
        const fileId = await upload.mutateAsync(file)
        setUploads((prev) => prev.map((u, i) => (i === idx ? { ...u, fileId } : u)))
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : 'Ошибка загрузки'
        setUploads((prev) => prev.map((u, i) => (i === idx ? { ...u, error: msg } : u)))
      }
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    create.mutate(
      {
        kind,
        comment: comment.trim() || undefined,
        file_ids: fileIds.length > 0 ? fileIds : undefined,
      },
      {
        onSuccess: () => {
          setComment('')
          setUploads([])
          onClose()
        },
      },
    )
  }

  return (
    <Sheet open={open} onClose={onClose} title="Новый отчёт">
      <form onSubmit={submit}>
        <Field label="Тип">
          <Select value={kind} onChange={(e) => setKind(e.target.value as ReportKind)}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {reportKindLabel[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Комментарий">
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
        <Field label="Фото / файлы">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => void onFiles(e)}
            className="block w-full text-sm text-tg-hint"
          />
          {uploads.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs">
              {uploads.map((u, i) => (
                <li key={i} className="flex items-center gap-2">
                  {u.fileId ? (
                    <span className="text-green-700">✓</span>
                  ) : u.error ? (
                    <span className="text-risk-red">✕</span>
                  ) : (
                    <Spinner size={12} />
                  )}
                  <span className="truncate text-tg-hint">{u.name}</span>
                  {u.error && <span className="text-risk-red">{u.error}</span>}
                </li>
              ))}
            </ul>
          )}
        </Field>

        {errMessage && (
          <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-risk-red">{errMessage}</p>
        )}
        <div className="flex gap-2">
          <Button type="button" variant="secondary" fullWidth onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" fullWidth loading={create.isPending} disabled={!canSubmit || uploading}>
            Отправить
          </Button>
        </div>
      </form>
    </Sheet>
  )
}
